import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';
import CrudApiClient from './crudapiclient';
import type { SearchQuery } from '../utils';

export type CollectionCsvExportRow = {
  id: string;
  name: string;
  description: string;
  useCase: string;
  sortBy: string;
  limit: string;
  pdpRank: string;
  pdpRankTitle: string;
  pdpRankSubtitle: string;
  headerTitle: string;
  headerTags: string;
  headerDescription: string;
  headerMedia: string;
  quickFilters: string;
  queryFilters: string;
  queryExclusions: string;
  advancedFilters: string;
  seoContent: string;
  seoDescription: string;
  seoKeywords: string;
  seoLinks: string;
};

type CollectionSearchResult = {
  id: string | number;
  slug?: string;
};

type CollectionSeoImportRow = {
  rowNumber?: number;
  slug: string;
  seoContent?: string;
  seoDescription?: string;
  seoKeywords?: string;
  seoLinks?: string;
};

type CollectionSeoImportResponseItem = {
  input: CollectionSeoImportRow;
  summary: {
    operation: string;
    updatedFields: string[];
  };
  remarks: string[];
};

class CollectionApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/catalog/collection');
  }

  async updateById<T = any>(
    id: string | number,
    data: Partial<T>,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    try {
      const existingCollection = await this.findById<Record<string, unknown>>(id, meta);
      const immutableData = { ...data } as Record<string, unknown>;
      const existingSlug = existingCollection?.slug;

      if (typeof existingSlug === 'string' && existingSlug.length > 0) {
        immutableData.slug = existingSlug;
      } else {
        delete immutableData.slug;
      }

      const response = await axiosInstance.put(`${this.baseURL}/${id}`, immutableData, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating collection:', error);
      throw error;
    }
  }

  async deleteById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.delete(`${this.baseURL}/${id}`, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting collection:', error);
      throw error;
    }
  }

  // Override the transform methods to add custom headers
  async create<T = any>(payload: Partial<T>, meta: MetaQuery | undefined): Promise<T> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}` + '/', payload, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating collection:', error);
      throw error;
    }
  }

  override async createMany<T = any>(
    payload: Array<Partial<T>>,
    meta: MetaQuery | undefined,
  ): Promise<Array<T>> {
    try {
      if (this.isSeoImportPayload(payload)) {
        const response = await this.updateCollectionSeoRows(payload as Array<CollectionSeoImportRow>, meta);
        return response as Array<T>;
      }

      const response = await axiosInstance.post(`${this.baseURL}/bulkUpsert`, payload, {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error bulk upserting collections:', error);
      throw error;
    }
  }

  private isSeoImportPayload(payload: Array<Partial<unknown>>): payload is Array<CollectionSeoImportRow> {
    return payload.length > 0 && payload.every(item => {
      const row = item as Record<string, unknown>;
      return (
        typeof row.slug === 'string' &&
        !('id' in row) &&
        !('name' in row) &&
        !('useCase' in row) &&
        !('sortBy' in row)
      );
    });
  }

  private async updateCollectionSeoRows(
    payload: Array<CollectionSeoImportRow>,
    meta: MetaQuery | undefined,
  ): Promise<Array<CollectionSeoImportResponseItem>> {
    const results: Array<CollectionSeoImportResponseItem> = [];

    for (const row of payload) {
      const slug = row.slug.trim();
      const collectionId = await this.findCollectionIdBySlug(slug);
      const existingCollection = await this.findById<Record<string, unknown>>(collectionId, meta);

      await this.updateById(
        collectionId,
        {
          ...existingCollection,
          seoContent: this.normalizeSeoContent(row.seoContent),
          seoDescription: this.normalizeSeoContent(row.seoDescription),
          seoKeywords: this.normalizeSeoKeywords(row.seoKeywords),
          seoLinks: this.normalizeSeoLinks(row.seoLinks),
        },
        meta,
      );

      results.push({
        input: row,
        summary: {
          operation: 'UPDATED',
          updatedFields: ['seoContent', 'seoDescription', 'seoKeywords', 'seoLinks'],
        },
        remarks: [],
      });
    }

    return results;
  }

  private async findCollectionIdBySlug(slug: string): Promise<string | number> {
    const response = await axiosInstance.post(
      `${this.baseURL}/search?page=1&size=1`,
      {
        filters: [{ field: 'slug', filterType: 'EQ', value: slug }],
      },
      {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      },
    );

    const collections = response.data?.data as Array<CollectionSearchResult> | undefined;
    const collection = collections?.[0];
    if (!collection?.id) {
      throw new Error(`Collection not found for slug: ${slug}`);
    }
    return collection.id;
  }

  private normalizeSeoContent(value: string | undefined): string | null {
    const trimmedValue = value?.trim();
    return trimmedValue ? trimmedValue : null;
  }

  private normalizeSeoKeywords(value: string | undefined): string[] {
    if (!value) return [];

    return value
      .split('|')
      .map(keyword => keyword.trim())
      .filter(Boolean);
  }

  private normalizeSeoLinks(value: string | undefined): Array<{ title: string; slug: string }> {
    if (!value?.trim()) return [];

    return value
      .split(';')
      .map(token => token.trim())
      .filter(Boolean)
      .map(token => {
        const [rawTitle = '', rawSlug = ''] = token.split('|');
        const title = rawTitle.trim();
        let slug = rawSlug.trim();

        if (slug.startsWith('https://coyu.com')) {
          slug = slug.slice('https://coyu.com'.length);
        }
        if (slug && !slug.startsWith('/')) {
          slug = `/${slug}`;
        }

        return { title, slug };
      })
      .filter(link => Boolean(link.title && link.slug));
  }

  async exportCsvRows(
    _sorters: unknown,
    _filters: unknown,
    payload: SearchQuery | undefined,
  ): Promise<CollectionCsvExportRow[]> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/export`, payload ?? {}, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const responsePayload = response.data?.data ?? response.data;
      const rows = Array.isArray(responsePayload) ? responsePayload : responsePayload?.data;

      if (!Array.isArray(rows)) {
        throw new Error('Collection export response did not contain a row list');
      }
      return rows;
    } catch (error) {
      console.error('Error exporting collections:', error);
      throw error;
    }
  }

  // Method to get facets for filtering
  async getFacets(): Promise<any> {
    try {
      const response = await axiosInstance.post(
        GATEWAY_API_URL + '/search/styles',
        {
          searchType: 'FREETEXT',
          text: '',
          selectedFacets: {},
          page: 1,
          size: 1,
        },
        {
          headers: {
            'x-lk-orgid': 'lyskraft',
            'x-lk-portalcode': 'pos',
            'x-lk-scid': '1',
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data.data.facets || [];
    } catch (error) {
      console.error('Error fetching facets:', error);
      throw error;
    }
  }
}

export default CollectionApiClient;
